import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import cloudApi from '../../../services/cloudApi';
import InviteMemberModal from './InviteMemberModal';

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;

const getInitials = (nameOrEmail) => {
  if (!nameOrEmail) return '?';
  const source = nameOrEmail.includes('@') ? nameOrEmail.split('@')[0] : nameOrEmail;
  return source.split(/[\s._-]+/).map((w) => w[0]).filter(Boolean).join('').toUpperCase().slice(0, 2);
};

const MemberAvatar = ({ name, email }) => (
  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 flex items-center justify-center flex-shrink-0">
    <span className="text-xs font-bold text-white">{getInitials(name || email)}</span>
  </div>
);

const Pill = ({ tone = 'neutral', children }) => {
  const toneClasses = {
    neutral: 'bg-gray-400/10 text-gray-500 dark:text-gray-400 ring-gray-400/20',
    success: 'bg-emerald-400/10 text-emerald-600 dark:text-emerald-400 ring-emerald-400/20',
    warning: 'bg-amber-400/10 text-amber-600 dark:text-amber-400 ring-amber-400/20',
    info: 'bg-blue-400/10 text-blue-600 dark:text-blue-400 ring-blue-400/20',
  };
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full ring-1 ring-inset font-semibold uppercase tracking-wide ${toneClasses[tone]}`}>
      {children}
    </span>
  );
};

const TeamSection = ({ workspace, onNotify }) => {
  const navigate = useNavigate();
  const { user, workspaces, switchWorkspace, refreshWorkspaces } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const pickerRef = useRef(null);
  const renameTriggerRef = useRef(null);
  const refocusTriggerRef = useRef(false);

  // Drop any in-flight rename when the active workspace changes, so a draft
  // can never be submitted against the workspace the user just switched away from.
  useEffect(() => {
    setRenaming(false);
    setNameDraft('');
  }, [workspace?.id]);

  // Close the workspace picker when clicking outside
  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pickerOpen]);

  // Order tabs the same way the top-bar dropdown does:
  // default workspace first, then Team, then Personal, alpha within group.
  const sortedWorkspaces = useMemo(() => {
    if (!workspaces || workspaces.length === 0) return [];
    const defaultId = user?.default_workspace_id;
    const isPersonalWs = (w) => w.role === 'owner' && Number(w.member_count) <= 1;
    return [...workspaces].sort((a, b) => {
      if (a.id === defaultId && b.id !== defaultId) return -1;
      if (b.id === defaultId && a.id !== defaultId) return 1;
      const aPersonal = isPersonalWs(a);
      const bPersonal = isPersonalWs(b);
      if (aPersonal !== bPersonal) return aPersonal ? 1 : -1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [workspaces, user?.default_workspace_id]);

  const handlePickWorkspace = async (workspaceId) => {
    setPickerOpen(false);
    if (workspaceId === workspace?.id || switching) return;
    setSwitching(true);
    try {
      await switchWorkspace(workspaceId);
      // Match the top-bar dropdown's reload-after-switch behavior so workspace-
      // scoped data across the dashboard refreshes consistently. The toast
      // persists across reload via sessionStorage.
      window.location.reload();
    } catch (err) {
      onNotify?.({ type: 'error', message: err.message || 'Failed to switch workspace' });
      setSwitching(false);
    }
  };

  const currentIndex = sortedWorkspaces.findIndex((w) => w.id === workspace?.id);
  const hasMultiple = sortedWorkspaces.length > 1;

  const isOwner = workspace?.role === 'owner' || workspace?.user_role === 'owner';
  const features = workspace?.features || {};
  const teamCollabEnabled = features.team_collab === true;
  const seatLimit = Number(features.default_seats) || 0;

  const loadMembers = useCallback(async () => {
    if (!workspace?.id) return;
    setLoading(true);
    try {
      const list = await cloudApi.getWorkspaceMembers(workspace.id);
      setMembers(list);
    } catch (err) {
      onNotify?.({ type: 'error', message: err.message || 'Failed to load members' });
    } finally {
      setLoading(false);
    }
  }, [workspace?.id, onNotify]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const { activeMembers, pendingInvites } = useMemo(() => {
    const active = members.filter((m) => m.invitation_status === 'accepted');
    const pending = members.filter((m) => m.invitation_status === 'pending');
    return { activeMembers: active, pendingInvites: pending };
  }, [members]);

  const seatsUsed = activeMembers.length + pendingInvites.length;

  const inviteDisabledReason = !teamCollabEnabled
    ? 'Team collaboration is not enabled for this workspace'
    : seatLimit > 0 && seatsUsed >= seatLimit
    ? `Seat limit reached (${seatsUsed}/${seatLimit})`
    : null;

  const inviteDisabledHelper = !teamCollabEnabled
    ? { lead: 'Team collaboration is included in the Team plan.', cta: 'Upgrade plan to add more members →' }
    : inviteDisabledReason
    ? { lead: `${inviteDisabledReason}.`, cta: 'Upgrade plan to add more members →' }
    : null;

  const handleInvited = (email) => {
    onNotify?.({ type: 'success', message: `Invitation sent to ${email}` });
    loadMembers();
  };

  const handleCancelInvite = async (invitationId) => {
    setActionId(invitationId);
    try {
      await cloudApi.cancelWorkspaceInvitation(workspace.id, invitationId);
      onNotify?.({ type: 'success', message: 'Invitation cancelled' });
      await loadMembers();
    } catch (err) {
      onNotify?.({ type: 'error', message: err.message || 'Failed to cancel invitation' });
    } finally {
      setActionId(null);
    }
  };

  const handleResend = async (invite) => {
    setActionId(invite.id);
    try {
      await cloudApi.cancelWorkspaceInvitation(workspace.id, invite.id);
      await cloudApi.inviteWorkspaceMember(workspace.id, invite.invitation_email);
      onNotify?.({ type: 'success', message: `Invitation resent to ${invite.invitation_email}` });
      await loadMembers();
    } catch (err) {
      onNotify?.({ type: 'error', message: err.message || 'Failed to resend invitation' });
    } finally {
      setActionId(null);
    }
  };

  const handleRemove = async (memberUserId) => {
    setActionId(memberUserId);
    try {
      await cloudApi.removeWorkspaceMember(workspace.id, memberUserId);
      onNotify?.({ type: 'success', message: 'Member removed' });
      setConfirmRemoveId(null);
      await loadMembers();
    } catch (err) {
      onNotify?.({ type: 'error', message: err.message || 'Failed to remove member' });
    } finally {
      setActionId(null);
    }
  };

  const trimmedDraft = nameDraft.trim();
  // Mirrors updateWorkspaceSchema on the backend: trimmed, 2..255 chars.
  // Duplicate workspace names are legal, so there is nothing else to check.
  const canSaveName =
    trimmedDraft.length >= 2 &&
    trimmedDraft.length <= 255 &&
    trimmedDraft !== (workspace?.name || '') &&
    actionId !== 'rename';

  const startRename = () => {
    setNameDraft(workspace?.name || '');
    setRenaming(true);
  };

  const closeRename = () => {
    setRenaming(false);
    setNameDraft('');
    // Focus can't be restored here: the trigger is unmounted while the form is
    // open, so its ref is null until React commits the read view. Flag it and
    // let the layout effect below move focus once the button exists again.
    refocusTriggerRef.current = true;
  };

  useLayoutEffect(() => {
    if (renaming || !refocusTriggerRef.current) return;
    refocusTriggerRef.current = false;
    renameTriggerRef.current?.focus();
  }, [renaming]);

  const handleRename = async (e) => {
    e.preventDefault();
    if (!isOwner || actionId === 'rename') return;
    const trimmed = nameDraft.trim();
    // Unchanged (or emptied) draft: just close, don't bump updated_at server-side
    // and don't claim a rename happened.
    if (!trimmed || trimmed === (workspace?.name || '')) {
      closeRename();
      return;
    }
    if (trimmed.length < 2 || trimmed.length > 255) return;

    setActionId('rename');
    try {
      await cloudApi.updateWorkspace(workspace.id, { name: trimmed });
    } catch (err) {
      // Stay in edit mode with the draft intact so the user can correct and retry.
      onNotify?.({ type: 'error', message: err.message || 'Failed to rename workspace' });
      return;
    } finally {
      setActionId(null);
    }

    onNotify?.({ type: 'success', message: `Workspace renamed to ${trimmed}` });
    closeRename();

    // Refetch rather than merging the PATCH response: that row carries no
    // role / features / member_count, which this section renders from. The
    // rename is already persisted at this point, so a failed refetch must not
    // be reported as a failed rename — every name render site catches up on
    // the next workspaces load.
    try {
      await refreshWorkspaces();
    } catch (err) {
      console.error('Workspace renamed but refresh failed:', err);
    }
  };

  const handleLeave = async () => {
    setActionId('leave');
    try {
      await cloudApi.leaveWorkspace(workspace.id);
      onNotify?.({ type: 'success', message: `You have left ${workspace.name}` });
      setConfirmLeave(false);
      await refreshWorkspaces();
    } catch (err) {
      onNotify?.({ type: 'error', message: err.message || 'Failed to leave workspace' });
      setActionId(null);
    }
  };

  return (
    <section className="rounded-2xl bg-white dark:bg-gray-900/40 ring-1 ring-gray-200 dark:ring-gray-800/70 shadow-sm overflow-hidden">
      <div className="p-6 border-b border-gray-100 dark:border-gray-800/70">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Team</p>
            <div className="mt-1 flex items-center gap-3 flex-wrap">
              {renaming ? (
                <form onSubmit={handleRename} className="flex-1 min-w-0 flex items-center gap-2">
                  <input
                    type="text"
                    autoFocus
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        closeRename();
                      }
                    }}
                    maxLength={255}
                    disabled={actionId === 'rename'}
                    aria-label="Workspace name"
                    className="flex-1 min-w-0 px-3 py-1.5 rounded-lg text-lg font-semibold ring-1 ring-gray-200 dark:ring-gray-700 focus:ring-2 focus:ring-cyan-500/60 bg-white dark:bg-gray-800/50 text-gray-900 dark:text-gray-100 outline-none disabled:opacity-50"
                  />
                  <button
                    type="submit"
                    disabled={!canSaveName}
                    className="text-sm px-3 py-1.5 rounded-lg font-medium text-white bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 disabled:opacity-50 transition-colors"
                  >
                    {actionId === 'rename' ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={closeRename}
                    disabled={actionId === 'rename'}
                    className="text-sm px-3 py-1.5 rounded-lg text-gray-600 dark:text-gray-300 ring-1 ring-gray-200 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50 transition-colors"
                  >
                    Cancel
                  </button>
                </form>
              ) : (
                <>
                  <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-gray-50 truncate">
                    {workspace?.name}
                  </h2>
                  {isOwner && (
                    <button
                      type="button"
                      ref={renameTriggerRef}
                      onClick={startRename}
                      aria-label="Rename workspace"
                      title="Rename workspace"
                      className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  )}
                </>
              )}
              <Pill tone={isOwner ? 'info' : 'neutral'}>{isOwner ? 'Owner' : 'Member'}</Pill>
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {activeMembers.length} {activeMembers.length === 1 ? 'member' : 'members'}
              {pendingInvites.length > 0 && ` · ${pendingInvites.length} pending`}
              {teamCollabEnabled && seatLimit > 0 && ` · ${seatsUsed} of ${seatLimit} seats used`}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
          {hasMultiple && (
            <div className="relative" ref={pickerRef}>
              <button
                type="button"
                onClick={() => setPickerOpen((o) => !o)}
                disabled={switching}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                aria-haspopup="listbox"
                aria-expanded={pickerOpen}
              >
                <span>Switch workspace</span>
                <span className="px-2 py-0.5 rounded-full bg-cyan-100 dark:bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 text-[11px] font-bold tabular-nums">
                  {sortedWorkspaces.length}
                </span>
                <svg
                  className={`w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-transform ${pickerOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {pickerOpen && (
                <div
                  role="listbox"
                  className="absolute right-0 top-full mt-1.5 z-20 w-72 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 shadow-xl shadow-black/5 dark:shadow-black/30 overflow-hidden animate-slide-up"
                >
                  <p className="px-3 pt-3 pb-1.5 text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-semibold">
                    Your workspaces
                  </p>
                  <ul className="pb-1.5 max-h-80 overflow-y-auto">
                    {sortedWorkspaces.map((w) => {
                      const isActive = w.id === workspace?.id;
                      const isOwnerRow = w.role === 'owner';
                      return (
                        <li key={w.id}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={isActive}
                            onClick={() => handlePickWorkspace(w.id)}
                            className={`w-full px-3 py-2 flex items-center gap-2.5 text-left transition-colors ${
                              isActive
                                ? 'bg-gray-50 dark:bg-gray-800/60'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'
                            }`}
                          >
                            <span className={`text-sm flex-1 truncate ${
                              isActive
                                ? 'text-gray-900 dark:text-white font-semibold'
                                : 'text-gray-700 dark:text-gray-200 font-medium'
                            }`}>
                              {w.name}
                            </span>
                            <span
                              className={`text-[9px] px-1.5 py-0.5 rounded-md font-semibold uppercase tracking-wide flex-shrink-0 ${
                                isOwnerRow
                                  ? 'bg-blue-400/15 text-blue-600 dark:text-blue-400'
                                  : 'bg-gray-400/15 text-gray-500 dark:text-gray-400'
                              }`}
                            >
                              {isOwnerRow ? 'Owner' : 'Member'}
                            </span>
                            {isActive && (
                              <svg className="w-4 h-4 text-cyan-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}

          {isOwner && (
            <>
              {inviteDisabledReason ? (
                <button
                  type="button"
                  onClick={() => navigate('/plans')}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 shadow-sm transition-colors"
                  title={inviteDisabledReason}
                >
                  Upgrade Plan
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setInviteOpen(true)}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 shadow-sm transition-colors"
                >
                  Invite member
                </button>
              )}
            </>
          )}
          </div>
        </div>

        {isOwner && inviteDisabledHelper && (
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            {inviteDisabledHelper.lead}{' '}
            <button
              type="button"
              onClick={() => navigate('/plans')}
              className="font-semibold bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent hover:from-blue-500 hover:via-cyan-500 hover:to-purple-500 transition-all"
            >
              {inviteDisabledHelper.cta}
            </button>
          </p>
        )}

        {!isOwner && (
          <div className="mt-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-200 dark:ring-blue-800 px-4 py-3 text-sm text-blue-800 dark:text-blue-200">
            To manage billing or invite teammates, contact your workspace owner.
          </div>
        )}
      </div>

      <div className="p-6">
        {loading ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Loading members...</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800/70">
            {activeMembers.map((m) => {
              const isSelf = m.user_id === user?.id;
              return (
                <li key={m.id} className="py-3 flex items-center gap-3">
                  <MemberAvatar name={m.user_name} email={m.user_email} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">
                      {m.user_name || m.user_email}
                      {isSelf && <span className="ml-2 text-xs text-gray-400">(you)</span>}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{m.user_email}</p>
                  </div>
                  <Pill tone={m.role === 'owner' ? 'info' : 'neutral'}>{m.role}</Pill>
                  {m.joined_at && (
                    <span className="hidden sm:inline text-xs text-gray-400 dark:text-gray-500">
                      Joined {formatDate(m.joined_at)}
                    </span>
                  )}
                  {isOwner && !isSelf && m.role !== 'owner' && (
                    confirmRemoveId === m.user_id ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleRemove(m.user_id)}
                          disabled={actionId === m.user_id}
                          className="text-xs px-2 py-1 rounded-md bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
                        >
                          {actionId === m.user_id ? 'Removing...' : 'Confirm'}
                        </button>
                        <button
                          onClick={() => setConfirmRemoveId(null)}
                          className="text-xs px-2 py-1 rounded-md text-gray-600 dark:text-gray-300 ring-1 ring-gray-200 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/60"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmRemoveId(m.user_id)}
                        className="text-xs px-2 py-1 rounded-md text-gray-500 dark:text-gray-400 ring-1 ring-gray-200 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/60"
                      >
                        Remove
                      </button>
                    )
                  )}
                </li>
              );
            })}
            {pendingInvites.map((inv) => (
              <li key={inv.id} className="py-3 flex items-center gap-3">
                <MemberAvatar email={inv.invitation_email} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">
                    {inv.invitation_email}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Invited {formatDate(inv.invited_at)}
                    {inv.expires_at && ` · expires ${formatDate(inv.expires_at)}`}
                  </p>
                </div>
                <Pill tone="warning">Pending</Pill>
                {isOwner && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleResend(inv)}
                      disabled={actionId === inv.id}
                      className="text-xs px-2 py-1 rounded-md text-gray-600 dark:text-gray-300 ring-1 ring-gray-200 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
                    >
                      Resend
                    </button>
                    <button
                      onClick={() => handleCancelInvite(inv.id)}
                      disabled={actionId === inv.id}
                      className="text-xs px-2 py-1 rounded-md text-red-600 dark:text-red-400 ring-1 ring-red-200 dark:ring-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </li>
            ))}
            {!loading && activeMembers.length === 0 && pendingInvites.length === 0 && (
              <li className="py-3 text-sm text-gray-400 dark:text-gray-500">No members yet.</li>
            )}
          </ul>
        )}

        {!isOwner && (
          <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-800/70">
            {confirmLeave ? (
              <div className="flex items-center gap-3">
                <p className="text-sm text-gray-600 dark:text-gray-300 flex-1">
                  Leave <span className="font-medium">{workspace.name}</span>? You'll lose access to its tasks and data.
                </p>
                <button
                  onClick={handleLeave}
                  disabled={actionId === 'leave'}
                  className="text-sm px-3 py-1.5 rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
                >
                  {actionId === 'leave' ? 'Leaving...' : 'Confirm leave'}
                </button>
                <button
                  onClick={() => setConfirmLeave(false)}
                  className="text-sm px-3 py-1.5 rounded-lg text-gray-600 dark:text-gray-300 ring-1 ring-gray-200 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/60"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmLeave(true)}
                className="text-sm px-3 py-1.5 rounded-lg text-red-600 dark:text-red-400 ring-1 ring-red-200 dark:ring-red-800 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                Leave workspace
              </button>
            )}
          </div>
        )}
      </div>

      <InviteMemberModal
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        workspaceId={workspace?.id}
        onInvited={handleInvited}
      />
    </section>
  );
};

export default TeamSection;
